import { environment } from 'src/environments/environment';
import { IBlockchainStatus, ICoinPrice, IFiatStatus } from '../models/Crypto';
import {
  ACCOUNT_CURRENCY,
  DEVICE_TYPE,
  DOCUMENT_TYPE,
  IComplianceChecks,
  ICuitByDni,
  IProviderData,
  IUserData,
  IUserInfoField,
  IUserPepData,
} from '../models/User';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { map, share } from 'rxjs/operators';
import { User } from '../models/User';
import { IAlertMessage } from '../models/AlertMessage';
import { ITransaction } from '../models/Transactions';
import { IOrderStatus } from '../models/Order';
import { Observable, of } from 'rxjs';
import { IGenerateQrResponse, ISuccessLogin } from '../models/Auth';
import { RegisterRequest } from '../models/Whitelabel';
import { IMinOperation, IPublicWithdraw } from '../../public/services/utils/public-data';
import {
  IBankAccount,
  IDataWidget,
  IFieldsPersonalData,
  IWidgetStatus,
  Status,
} from '../models/WidgetUser';
import { IOperationStatus } from '../../transactions/models/operations';
import { DOCUMENT_TYPE_PHL, EXCHANGE, EXCHANGE_COUNTRY, LEGAL_ID_TYPE } from '../models/Onboarding';
import { IPaymentLockResponse } from '../models/Payment';
import { IDocument } from '../models/DocumentFlow';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(public http: HttpClient) {}

  getTokens(): { jwtToken: string; refreshToken: string } {
    const tokens = { jwtToken: '', refreshToken: '' };
    tokens.jwtToken = localStorage.getItem('JWT_TOKEN');
    tokens.refreshToken = localStorage.getItem('REFRESH_TOKEN');
    return tokens;
  }

  //////////////////////////////////////////////////////////
  /////////////////// ---- TC APIs ---- /////////////////////

  register(email: string, password: string, ref: string = '', prodeReferredBy: string = '') {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .post<User>(
        environment.tcApiUrl + 'user',
        { email, password, ref, prodeReferredBy },
        { headers },
      )
      .pipe(share());
  }

  registerWorldCoin(data: RegisterRequest) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');

    if (data.address) data.address = data.address.toLocaleLowerCase();
    if (!data.exchangeCountry) data.exchangeCountry = 'ARG';
    if (!data.address) data.address = data.userId;

    return this.http
      .post<{ accessToken: string; refreshToken: string }>(
        environment.tcApiUrl + 'user/whitelabel',
        data,
        { headers },
      )
      .pipe(share());
  }

  crossLogin(humanToken: string): Observable<IGenerateQrResponse> {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    const params = new HttpParams().append('humanToken', humanToken);

    return this.http.get<IGenerateQrResponse>(environment.tcApiUrl + 'auth/crossLogin', {
      headers,
      params,
    });
  }

  login(email: string, password: string, fingerprint: string, token: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    const obs = this.http
      .post<{
        accessToken: string;
        refreshToken: string;
        user: User;
        complianceChecks: IComplianceChecks;
      }>(
        environment.tcApiUrl + 'auth/login',
        { email, password, fingerprint, humanToken: token },
        { ...headers, observe: 'response' },
      )
      .pipe(share());
    return obs;
  }

  getMe() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http.get<any>(environment.tcApiUrl + 'user', { headers }).pipe(share());
  }

  updateUserInfo(fields: IUserInfoField, updatePersonalDataLastCheckFlag: boolean = false) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .patch<any>(
        environment.tcApiUrl + 'user',
        { fields, updatePersonalDataLastCheckFlag },
        { headers },
      )
      .pipe(share());
  }

  updateUserWork(desc: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .put<void>(environment.tcApiUrl + 'user/compliance/work', { desc }, { headers })
      .pipe(share());
  }

  createMarketOrder(amount: string, coin: string, operation: string, code: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<any>(
        environment.tcApiUrl + 'order',
        {
          amount,
          coin,
          operation, // ['BUY' | 'SELL']
          code,
        },
        { headers },
      )
      .pipe(share());
  }

  getAlertMessage() {
    return this.http.get<IAlertMessage>(environment.tcApiUrl + 'settings/alerts');
  }

  getPromotionMessages() {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.get(environment.tcApiUrl + 'settings/promotions?platform=2', { headers });
  }

  lockMarketOrder(coin: string, operation: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<any>(
        environment.tcApiUrl + 'order/lock',
        {
          coin,
          operation, // ['BUY' | 'SELL']
        },
        { headers },
      )
      .pipe(share());
  }

  getAllPrices() {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.get<any>(environment.tcApiUrl + 'price/all', { headers }).pipe(share());
  }

  getPriceByCoin(coin: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<any>(environment.tcApiUrl + 'price/coin/' + coin + '/worldcoin', { headers })
      .pipe(share());
  }

  getPriceByCoinWld(coin: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<any>(environment.tcApiUrl + 'price/coin/' + coin, { headers })
      .pipe(share());
  }

  getFiatStatus() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<IFiatStatus[]>(environment.tcApiUrl + `settings/fiat/status`, { headers })
      .pipe(share());
  }

  updateUserPepInfo(pepData: IUserPepData) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .put<void>(
        environment.tcApiUrl + 'user/pep',
        {
          pepData,
        },
        { headers },
      )
      .pipe(share());
  }

  // /user/limits

  /////////////////// ---- END TC APIs ---- /////////////////
  //////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////
  //////////////// ---- BANK / ACCOUNT ---- ////////////////

  addAccount(
    currency: string,
    cbu: string,
    description: string,
    bankCode?: string,
    accountType?: string,
    retryFailed?: boolean,
  ) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<any>(
        environment.tcApiUrl + 'user/bankAccount/' + currency,
        {
          cbu,
          description,
          bankCode,
          accountType,
          retryFailed,
        },
        { headers },
      )
      .pipe(share());
  }

  addAccountWithCode(
    currency: string,
    cbu: string,
    description: string,
    bankCode?: string,
    accountType?: string,
    publicAccessHash?: string,
    accountChangeCode?: string,
  ) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .put<any>(
        environment.apiManteca + `public/withdraw/${publicAccessHash}/account`,
        {
          cbu,
          description,
          bankCode,
          accountType,
          accountChangeCode,
          coin: currency,
        },
        { headers },
      )
      .pipe(share());
  }

  /////////////// ---- BANK / ACCOUNT END ---- //////////////
  //////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////
  //////////////// ---- ONBOARDING / ACCOUNT ---- /////////////

  getUploadImageURL(
    docType: DOCUMENT_TYPE,
    fileName: string,
    deviceType: DEVICE_TYPE,
    isArbitrage = false,
    extraData?: { phlDocumentType: DOCUMENT_TYPE_PHL },
  ) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<{ url: string }>(
        environment.tcApiUrl + 'documentation/uploadUrl',
        { docType, fileName, deviceType, isArbitrage, extraData },
        { headers },
      )
      .pipe(share());
  }

  uploadImageToUrl(url: string, image: File): any {
    /* El Content-Type va firmado en la URL: si no coincide, S3 responde 403. */
    const headers = new HttpHeaders({ 'Content-Type': image.type });

    return this.http.put<any>(url, image, { headers }).pipe(share());
  }

  //////////////// ---- ONBOARDING / ACCOUNT END---- /////////////
  /////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////
  /////////////////// ---- CRYPTO ---- ////////////////////

  sendWithdrawEmailVerification(params: {
    address: string;
    amount: string;
    coin: string;
    chain: string;
    costCode: string;
  }) {
    const { address, costCode, amount, coin, chain } = params;
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<any>(
        environment.tcApiUrl + 'withdraw/pre',
        {
          address,
          amount,
          coin,
          chain,
          costCode,
        },
        { headers },
      )
      .pipe(share());
  }

  withdrawCrypto(code: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<ITransaction>(
        environment.tcApiUrl + 'withdraw',
        {
          code,
        },
        { headers },
      )
      .pipe(share());
  }

  getWithdrawCost(ticker: string, wallet: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<any>(environment.tcApiUrl + `withdraw/estimate?coin=${ticker}&address=${wallet}`, {
        headers,
      })
      .pipe(share());
  }

  getBlockchainStatus() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<IBlockchainStatus[]>(environment.tcApiUrl + `settings/crypto/status`, { headers })
      .pipe(share());
  }

  getOrdersStatus() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<IOrderStatus>(environment.tcApiUrl + `settings/order/status`, { headers })
      .pipe(share());
  }

  ///////////////// ---- CRYPTO END ---- ///////////////////
  /////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////
  ///////////////////// ---- AUTH ---- ////////////////////

  checkExistUser(repCuit: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .put<any>(
        environment.tcApiUrl + 'user/rep',
        {
          repCuit,
        },
        { headers },
      )
      .pipe(share());
  }

  ///////////////////// ---- AUTH END---- //////////////////////
  /////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////
  ///////////////////// ---- 2FA ---- //////////////////////

  show2FAQr() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http.post<any>(environment.tcApiUrl + 'auth/2FA', {}, { headers }).pipe(share());
  }

  verifyActivation2FA(token: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<any>(environment.tcApiUrl + 'auth/verify2FA', { token }, { headers })
      .pipe(share());
  }

  verifyLogin2FA(
    apiUrl: string,
    token: string,
  ): Observable<{ body: ISuccessLogin; status: number } | HttpResponse<{}> | Error> {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .post(environment.tcApiUrl + apiUrl, { token }, { headers, observe: 'response' })
      .pipe(share());
  }

  verifyDeactivation2FA(token: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);

    return this.http
      .delete<any>(environment.tcApiUrl + `auth/2FA?token=${token}`, { headers })
      .pipe(share());
  }

  isMarketClosed() {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<any>(environment.tcApiUrl + 'settings/closed', { headers })
      .pipe(map((res) => res.closed))
      .pipe(share());
  }

  sendTikTokEvent(body) {
    const headers = new HttpHeaders()
      .append('Content-type', 'application/json')
      .append('Access-Token', this.getTokens().jwtToken);
    return this.http.post<any>(
      environment.tcApiUrl + 'tiktok/track',
      {
        ...body,
      },
      {
        headers,
      },
    );
  }

  lockPriceWhitelabel(
    coin: 'USDC' | 'WLD' | 'USDT' | 'USDCB',
    amount: number,
    lockType: 'ADDRESS' | 'CUIT',
    against: ACCOUNT_CURRENCY = ACCOUNT_CURRENCY.ARS,
    chain: number,
    payload: { externalId: string | undefined; withdrawType: string },
  ) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<{
        price: number;
        expires: string;
        totalFee: number;
        networkFee?: number;
        depositAddress?: string;
        legalEntity?: string;
      }>(
        environment.tcApiUrl + `order/lock/whitelabel`,
        {
          lockType,
          coin: coin + '_' + against,
          amount: +amount,
          chain: chain,
          payload,
        },
        {
          headers,
        },
      )
      .pipe(share());
  }

  getTransaction(hash: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<IPublicWithdraw>(environment.tcApiUrl + `withdraw/${hash}/public`, { headers })
      .pipe(share());
  }

  getPublicWithdraw(legalId: string, wallet: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<IPublicWithdraw>(
        environment.apiManteca + `public/withdraw?legalId=${legalId}&address=${wallet}`,
        { headers },
      )
      .pipe(share());
  }

  getPriceCoin(companyId: string, coin: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');

    return this.http
      .get<ICoinPrice>(
        environment.apiManteca + `public/price/${coin}/worldcoin?companyId=${companyId}`,
        { headers },
      )
      .pipe(share());
  }

  getJwtWithUuid(uuid: string) {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .get<{ token: string }>(environment.tcApiUrl + `auth/token/` + uuid, { headers })
      .pipe(share());
  }

  getMinOperation(pair: string, companyId: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<IMinOperation>(environment.apiManteca + `public/company/${companyId}/${pair}/limits`, {
        headers,
      })
      .pipe(share());
  }

  getUserLimits() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http.get<any>(environment.tcApiUrl + `user/limits`, { headers }).pipe(share());
  }

  getStatusUser(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .get<IWidgetStatus>(environment.apiManteca + `widget/onboarding/status`, {
        headers,
      })
      .pipe(share());
  }

  getCuitWithCbu(cbu: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .get<any>(environment.tcApiUrl + `user/bankInfo/${cbu}`, {
        headers,
      })
      .pipe(share());
  }

  getAfipData() {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http.get<any>(environment.tcApiUrl + 'user/afipData', { headers }).pipe(share());
  }

  getCuitByDniWidget(dni: string, signature: string, sex?: 'MALE' | 'FEMALE') {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .get<ICuitByDni>(environment.apiManteca + `widget/onboarding/cuitByDni/${dni}`, {
        headers,
        params: sex ? { sex } : {},
      })
      .pipe(share());
  }

  getDocuments(documentAnyId: string, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .get<IDocument[]>(environment.apiManteca + `widget/onboarding/documents/${documentAnyId}`, {
        headers,
      })
      .pipe(share());
  }

  getOperationStatus(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .get<IOperationStatus>(environment.apiManteca + `widget/operation/status`, {
        headers,
      })
      .pipe(share());
  }

  getPaymentStatus(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .get<IOperationStatus>(environment.apiMantecav2 + `widgets/payment/status`, {
        headers,
      })
      .pipe(share());
  }

  registerUser(
    signature: string,
    email: string,
    legalId: string,
    exchange: EXCHANGE,
    legalIdType?: LEGAL_ID_TYPE,
    isPPT?: boolean,
  ) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IDataWidget>(
        environment.apiManteca + 'widget/onboarding/initial',
        {
          email,
          legalId,
          legalIdType,
          exchange,
          isPPT,
        },
        { headers },
      )
      .pipe(share());
  }

  updatePersonalData(fields: IFieldsPersonalData, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IDataWidget>(environment.apiManteca + 'widget/onboarding/personalData', fields, {
        headers,
      })
      .pipe(share());
  }

  addBankAccount(bankAccount: IBankAccount, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IDataWidget>(
        environment.apiManteca + 'widget/onboarding/banking',
        { bankAccount },
        {
          headers,
        },
      )
      .pipe(share());
  }

  addBankAccountWidgetV1(bankAccount: IBankAccount) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<IDataWidget>(environment.tcApiUrl + 'widget/onboarding/add-bank-account', bankAccount, {
        headers,
      })
      .pipe(share());
  }

  getPresignedUrl(docType: DOCUMENT_TYPE, fileName: string, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<{ url: string }>(
        environment.apiManteca + 'widget/onboarding/documentation',
        { docType, fileName },
        {
          headers,
        },
      )
      .pipe(share());
  }

  getTruoraData(legalId: string, exchangeCountry?: EXCHANGE_COUNTRY) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    const params = new HttpParams({
      fromObject: {
        exchangeCountry: exchangeCountry,
      },
    });
    return this.http
      .get<IProviderData>(environment.tcApiUrl + `user/${legalId}/providerUserData`, {
        headers,
        params,
      })
      .pipe(share());
  }

  getUserDataBr(legalId: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    const params = new HttpParams({
      fromObject: {
        exchangeCountry: EXCHANGE_COUNTRY.BRA,
      },
    });
    return this.http
      .get<IProviderData>(environment.tcApiUrl + `user/${legalId}/providerUserData`, {
        headers,
        params,
      })
      .pipe(share());
  }

  lockPriceWidget(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IOperationStatus>(
        environment.apiManteca + 'widget/operation/lock',
        {},
        {
          headers,
        },
      )
      .pipe(share());
  }

  checkCompliance(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IOperationStatus>(
        environment.apiManteca + 'widget/operation/compliance',
        {},
        {
          headers,
        },
      )
      .pipe(share());
  }

  sendRules(signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    return this.http
      .post<IOperationStatus>(
        environment.apiManteca + 'widget/operation/rules',
        {},
        {
          headers,
        },
      )
      .pipe(share());
  }

  sendCodeSMS(phoneNumber: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    return this.http
      .post<boolean>(
        environment.tcApiUrl + 'user/phoneNumber/send',
        {
          phoneNumber,
        },
        { headers },
      )
      .pipe(share());
  }

  confirmPhoneNumber(code: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('x-access-token', this.getTokens().jwtToken);
    const params = new HttpParams().set('code', code);
    return this.http
      .post<boolean>(environment.tcApiUrl + 'user/phoneNumber/confirm', {}, { headers, params })
      .pipe(share());
  }

  sheetSaveForm(nombre: string, apellido: string, email: string, company: string = '') {
    const url = `${environment.urlFillSheet}?nombre=${nombre}&apellido=${apellido}&email=${email}&company=${company}`;

    return this.http.get(url);
  }

  lockPayment(paymentDestination: string, amount: number | null, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature ? signature : '');
    return this.http
      .post<IPaymentLockResponse>(
        environment.apiMantecav2 + 'widgets/payment/lock',
        { paymentDestination, amount },
        { headers },
      )
      .pipe(share());
  }

  createPayment(txId: string, signature: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('signature', signature);
    const body: any = {};
    body.txId = txId;
    return this.http
      .post<any>(environment.apiMantecav2 + 'widgets/payment/create', body, { headers })
      .pipe(share());
  }
}
